import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import moment from 'moment';
import { Subscription } from 'rxjs';
import { isEmptyOrNull } from '../../utils';
import { PlatformLicenseActivationDTO } from '@iota/iota-management-portal-v4-client';

// Type-safe form
type PlatformLicenseActivationControls = Partial<Record<keyof PlatformLicenseActivationDTO, FormControl>>;

@Component({
    standalone: false,
    selector: 'app-platform-license-activation-form',
    templateUrl: './platform-license-activation-form.component.html',
    styleUrls: ['./platform-license-activation-form.component.scss']
})
export class PlatformLicenseActivationFormComponent implements OnInit, OnChanges, OnDestroy {
    @Input() platformLicenseActivation: PlatformLicenseActivationDTO;
    @Input() editMode = true;
    @Output() formValid = new EventEmitter<boolean>();

    formSubscription: Subscription;
    platformLicenseActivationForm = this.fb.group<PlatformLicenseActivationControls>(
        {
            effectiveFrom: new FormControl<Date>(null, { validators: [Validators.required] }),
            effectiveTo: new FormControl<Date>(null)
        },
        {
            validators: [this.checkDateValidity]
        }
    );

    constructor(private fb: FormBuilder) {}

    ngOnInit(): void {
        this.formSubscription = this.platformLicenseActivationForm.valueChanges.subscribe(() => {
            this.formValid.emit(this.platformLicenseActivationForm.valid);
        });
        this.initForm();
    }

    ngOnChanges(changes: SimpleChanges): void {
        const previousLicenseActivation = changes?.platformLicenseActivation?.previousValue;
        const currentLicenseActivation = changes?.platformLicenseActivation?.currentValue;

        if (previousLicenseActivation !== currentLicenseActivation) {
            this.initForm();
        }
    }

    ngOnDestroy(): void {
        this.formSubscription?.unsubscribe();
    }

    initForm() {
        const platformLicenseActivationToUpdate: PlatformLicenseActivationDTO = !!this.platformLicenseActivation
            ? { ...this.platformLicenseActivation }
            : {};
        this.platformLicenseActivationForm.patchValue(platformLicenseActivationToUpdate);

        if (this.editMode) {
            this.platformLicenseActivationForm.enable();
        } else {
            this.platformLicenseActivationForm.disable();
        }
    }

    checkDateValidity(group: FormGroup<PlatformLicenseActivationControls>): ValidationErrors | null {
        const effectiveFrom = moment(group.value.effectiveFrom, 'YYY-MM-DD');
        const effectiveTo = moment(group.value.effectiveTo, 'YYY-MM-DD');

        let errors = {};

        if (effectiveTo.isBefore(effectiveFrom)) {
            errors = { ...errors, effectiveToIsBeforeEffectiveFrom: true };
        }

        if (isEmptyOrNull(errors)) return null;

        return errors;
    }

    getUpsertedPlatformLicenseActivation(): PlatformLicenseActivationDTO {
        const currentPlatformLicenseActivation = this.platformLicenseActivation ?? {};
        if (this.platformLicenseActivationForm.valid) {
            return { ...currentPlatformLicenseActivation, ...this.platformLicenseActivationForm.value };
        }

        return currentPlatformLicenseActivation;
    }
}
